import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { CommunicationsPageShell } from "../shared/CommunicationsPageShell";
import { useCommunicationDetail } from "../catalog/use-communications";
import {
  activeTranslation,
  findChannel,
  versionsForLocale,
} from "../shared/communication-selectors";
import { BODY_MAX, SUBJECT_MAX, channelHasSubject } from "../shared/communications-types";
import { resolveCommunicationError } from "../shared/resolve-communication-error";
import { useUpdateTranslation } from "./use-translations";
import { VersionList } from "./VersionList";
import { PreviewPanel } from "./PreviewPanel";

export function TranslationEditorPage() {
  const { id, channelId, locale } = useParams<{
    id: string;
    channelId: string;
    locale: string;
  }>();
  const { t } = useTranslation();

  const { communication, isLoading } = useCommunicationDetail(id);
  const channel = findChannel(communication, channelId);
  const versions = versionsForLocale(channel, locale);
  const active = activeTranslation(channel, locale);
  const needsSubject = channel ? channelHasSubject(channel.channel) : false;

  const update = useUpdateTranslation();

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever the active version changes (initial load, or after a
  // save creates + activates a new version that the list refetch surfaces).
  useEffect(() => {
    if (active) {
      setSubject(active.subject ?? "");
      setBody(active.body);
      setError(null);
    }
  }, [active]);

  const shell = (children: React.ReactNode) => (
    <CommunicationsPageShell
      screenTestId="communication-translation-editor-screen"
      titleKey="communications.translations.editorTitle"
      subtitleKey="communications.translations.editorSubtitle"
    >
      <div>
        <Link
          to={`/communications/${id}`}
          data-testid="communication-translation-back-link"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          {t("communications.translations.backToDetail")}
        </Link>
      </div>
      {children}
    </CommunicationsPageShell>
  );

  if (isLoading) {
    return shell(
      <p
        data-testid="communication-translation-editor-loading"
        className="text-sm text-muted-foreground"
      >
        {t("communications.detail.loading")}
      </p>,
    );
  }

  if (!communication || !channel || !active) {
    return shell(
      <p data-testid="communication-translation-not-found" className="text-sm text-destructive">
        {t("communications.translations.notFound")}
      </p>,
    );
  }

  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  const subjectChanged = needsSubject && trimmedSubject !== (active.subject ?? "");
  const bodyChanged = trimmedBody !== active.body;
  const isDirty = subjectChanged || bodyChanged;
  const isValid =
    trimmedBody.length > 0 &&
    trimmedBody.length <= BODY_MAX &&
    (!needsSubject || (trimmedSubject.length > 0 && trimmedSubject.length <= SUBJECT_MAX));

  const handleSave = async () => {
    if (!isDirty || !isValid || update.isPending) return;
    setError(null);
    try {
      await update.mutateAsync({
        translationId: active.id,
        input: {
          subject: subjectChanged ? trimmedSubject : undefined,
          body: bodyChanged ? trimmedBody : undefined,
        },
      });
    } catch (err) {
      setError(resolveCommunicationError(err, t));
    }
  };

  return shell(
    <>
      <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
        <p>
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
            {communication.key}
          </code>{" "}
          · {channel.channel} ·{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
            {active.localeCode}
          </code>{" "}
          · {t("communications.translations.versionLabel", { version: active.version })}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          {needsSubject && (
            <div className="space-y-2">
              <Label htmlFor="tr-subject">{t("communications.translations.subjectField")}</Label>
              <Input
                id="tr-subject"
                data-testid="communication-translation-subject-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={SUBJECT_MAX}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="tr-body">{t("communications.translations.bodyField")}</Label>
            <Textarea
              id="tr-body"
              data-testid="communication-translation-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={BODY_MAX}
              rows={16}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t("communications.translations.handlebarsHint")}
            </p>
          </div>

          {error && (
            <p data-testid="communication-translation-error" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button
              data-testid="communication-translation-save"
              onClick={handleSave}
              disabled={!isDirty || !isValid || update.isPending}
            >
              {update.isPending
                ? t("communications.actions.saving")
                : t("communications.actions.save")}
            </Button>
            {isDirty && (
              <p className="text-xs text-muted-foreground">
                {t("communications.translations.dirtyHint")}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <PreviewPanel
            communicationId={communication.id}
            channel={channel.channel}
            subject={subject}
            body={body}
          />
          <VersionList versions={versions} />
        </div>
      </div>
    </>,
  );
}
